import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Building2, CheckCircle, Info, Clock, Bot } from 'lucide-react';

const Reclassification = ({ uploadedFiles = [] }) => {
  const [showTooltip, setShowTooltip] = useState(null);

  const completedSteps = [
    {
      id: 1,
      title: 'Obtener valores reportados',
      description: 'Extrae el valor total de efectivo desde el estado financiero y lo compara con el valor declarado en la Nota 4',
      action: 'Acción IA:',
      results: [
        '→ Total efectivo reportado en estado financiero 2022: $4.363.663',
        '→ Total efectivo en Nota 4: $4.363.663',
        '✅ Coincidencia detectada'
      ]
    },
    {
      id: 2,
      title: 'Detectar subcategorías',
      description: 'Escanea la Nota 4 y detecta los siguientes componentes',
      action: 'Acción IA:',
      results: [
        'Cuentas corrientes:',
        '  • Disponibles: $15.129',
        '  • Restringidas: $438.118',
        'Cuentas de ahorros:',
        '  • Disponibles: $723.394',
        '  • Restringidas: $2.361.919'
      ]
    },
    {
      id: 3,
      title: 'Calcular efectivo restringido',
      description: 'Suma todas las subcuentas restringidas',
      action: 'Acción IA:',
      results: [
        '→ Total restringido = $438.118 + $2.361.919 = $2.800.037'
      ]
    },
    {
      id: 4,
      title: 'Ajustar clasificación',
      description: 'Resta $2.800.037 del total de "Efectivo" en Activo Circulante y agrega ese valor a "Efectivo restringido" en Activo No Circulante',
      action: 'Acción IA:',
      results: [
        '  Activo Circulante / Efectivo: 2023 $815,826 - 2022 $738,542',
        '  Activo No Circulante / Efectivo restringido: 2023 $ 3,547,837 - 2022 $ 2,800,037',
        '✅ Esta misma operación se repite automáticamente para el año 2023'
      ]
    },
    {
      id: 5,
      title: 'Leer el contenido de la nota',
      description: 'Detecta las dos subcategorías listadas en la Nota 5: Activos financieros',
      action: 'Acción IA:',
      results: [
        '• CDT (Certificados de Depósito): $273.851',
        '• Derechos fiduciarios: $466.579'
      ],
      isNote5: true
    },
    {
      id: 6,
      title: 'Verificar tratamiento contable',
      description: 'Verifica si existe instrucción de reclasificación por parte de la nota',
      action: 'Acción IA:',
      results: [
        '→ Nota indica que ambos son instrumentos de inversión',
        '→ No hay redirección ni cambio de categoría'
      ],
      isNote5: true
    },
    {
      id: 7,
      title: 'Validar clasificación existente',
      description: 'Confirma que los valores ya están en la cuenta de "Inversiones temporales" del Activo Circulante',
      action: 'Acción IA:',
      results: [
        '✅ No se realizan cambios. El agente documenta que la clasificación es correcta'
      ],
      isNote5: true
    }
  ];

  const investmentData = [
    {
      id: 'cdt',
      name: 'CDT a 180 días',
      amount: 273851,
      classification: 'Inversiones temporales',
      description: 'Certificado de Depósito a Término',
      plazo: '180 días',
      naturaleza: 'Depósito bancario',
      icon: Calendar,
      color: '#3b82f6'
    },
    {
      id: 'fiducias',
      name: 'Fiducias',
      amount: 466579,
      classification: 'Inversiones temporales',
      description: 'Fondos fiduciarios',
      plazo: 'Corto plazo',
      naturaleza: 'Fideicomiso de inversión',
      icon: Building2,
      color: '#8b5cf6'
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('COP', '$');
  };

  const totalInvestments = investmentData.reduce((sum, inv) => sum + inv.amount, 0);

  return (
    <div style={{ padding: '24px', width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', paddingBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: 'white', padding: '12px', borderRadius: '12px' }}>
          <Bot size={24} />
        </div>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
            Reclasificación Automática de Efectivo
          </h2>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '16px' }}>
            Análisis basado en Nota 4 (página 25) - Efectivo y equivalentes al efectivo
          </p>
          <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#f3e8ff',
              color: '#7c3aed',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              border: '1px solid #c4b5fd'
            }}>
              📄 Ver Nota 4 en panel lateral →
            </div>
            
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#ecfdf5',
              color: '#065f46',
              padding: '4px 8px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              border: '1px solid #86efac'
            }}>
              ✅ Mostrando documento específico de Nota 4
            </div>
          </div>
        </div>
      </div>

      {/* All Completed Steps Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: '2px solid #e2e8f0',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px'
        }}
      >
        <h3 style={{ 
          margin: '0 0 20px 0', 
          fontSize: '18px', 
          fontWeight: '600', 
          color: '#1f2937',
          textAlign: 'center'
        }}>
          Análisis IA Completado - Todos los Pasos (1-7)
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {completedSteps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              style={{
                background: step.isNote5 
                  ? 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)' 
                  : 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                border: `1px solid ${step.isNote5 ? '#c4b5fd' : '#86efac'}`,
                borderRadius: '12px',
                padding: '16px',
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
              }}
            >
              {/* Subtle background decoration */}
              <div style={{
                position: 'absolute',
                top: '-10px',
                right: '-10px',
                width: '40px',
                height: '40px',
                background: step.isNote5 
                  ? 'rgba(139, 92, 246, 0.08)' 
                  : 'rgba(16, 185, 129, 0.08)',
                borderRadius: '50%'
              }} />

              <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Compact Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{
                    background: step.isNote5 ? '#8b5cf6' : '#10b981',
                    color: 'white',
                    padding: '6px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '28px',
                    height: '28px',
                    fontSize: '12px',
                    fontWeight: '700'
                  }}>
                    {step.id}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <h4 style={{ 
                        margin: 0, 
                        fontSize: '15px', 
                        fontWeight: '600',
                        color: step.isNote5 ? '#7c3aed' : '#065f46'
                      }}>
                        {step.isNote5 ? '🟡' : ''} {step.title}
                      </h4>
                      {step.isNote5 && (
                        <div style={{
                          background: '#f3e8ff',
                          color: '#7c3aed',
                          padding: '2px 6px',
                          borderRadius: '8px',
                          fontSize: '9px',
                          fontWeight: '600',
                          border: '1px solid #c4b5fd'
                        }}>
                          Nota 5
                        </div>
                      )}
                    </div>
                    
                    <p style={{ 
                      margin: 0, 
                      fontSize: '12px',
                      color: step.isNote5 ? '#8b5cf6' : '#047857',
                      lineHeight: '1.3'
                    }}>
                      {step.description}
                    </p>
                  </div>

                  {/* Compact status badge */}
                  <div style={{
                    background: step.isNote5 ? '#f3e8ff' : '#d1fae5',
                    color: step.isNote5 ? '#7c3aed' : '#065f46',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '10px',
                    fontWeight: '600',
                    border: `1px solid ${step.isNote5 ? '#c4b5fd' : '#86efac'}`
                  }}>
                    ✓
                  </div>
                </div>

                {/* Compact Results */}
                <div style={{
                  background: step.isNote5 
                    ? 'rgba(139, 92, 246, 0.04)' 
                    : 'rgba(16, 185, 129, 0.04)',
                  border: `1px solid ${step.isNote5 
                    ? 'rgba(139, 92, 246, 0.15)' 
                    : 'rgba(16, 185, 129, 0.15)'}`,
                  borderRadius: '6px',
                  padding: '10px'
                }}>
                  <div style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: step.isNote5 ? '#7c3aed' : '#065f46',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Resultados:
                  </div>
                  {step.results.map((result, resultIndex) => (
                    <div
                      key={resultIndex}
                      style={{
                        fontSize: '11px',
                        color: step.isNote5 ? '#6b46c1' : '#047857',
                        marginBottom: resultIndex < step.results.length - 1 ? '2px' : 0,
                        fontFamily: 'monospace',
                        lineHeight: '1.3'
                      }}
                    >
                      {result}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <AnimatePresence>
        {/* Investment Cards */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}
        >
          {investmentData.map((investment, index) => {
            const Icon = investment.icon;
            
            return (
              <motion.div
                key={investment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.3 }}
                style={{
                  background: 'white',
                  border: `2px solid ${investment.color}`,
                  borderRadius: '16px',
                  padding: '24px',
                  boxShadow: `0 8px 24px ${investment.color}20`,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Background Pattern */}
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '120px',
                  height: '120px',
                  background: `linear-gradient(135deg, ${investment.color}15, ${investment.color}05)`,
                  borderRadius: '50%'
                }} />

                {/* Content */}
                <div style={{ position: 'relative', zIndex: 1 }}>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        background: investment.color,
                        color: 'white',
                        padding: '12px',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Icon size={24} />
                      </div>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
                          {investment.name}
                        </h3>
                        <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                          {investment.description}
                        </p>
                      </div>
                    </div>

                    {/* Info Tooltip */}
                    <div 
                      style={{ position: 'relative' }}
                      onMouseEnter={() => setShowTooltip(investment.id)}
                      onMouseLeave={() => setShowTooltip(null)}
                    >
                      <Info 
                        size={20} 
                        style={{ color: investment.color, cursor: 'pointer' }}
                      />
                      
                      {showTooltip === investment.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          style={{
                            position: 'absolute',
                            top: '30px',
                            right: '0',
                            background: '#1f2937',
                            color: 'white',
                            padding: '12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            width: '200px',
                            zIndex: 10,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                          }}
                        >
                          <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                            Clasificación correcta según:
                          </div>
                          <div>• Plazo: {investment.plazo}</div>
                          <div>• Naturaleza: {investment.naturaleza}</div>
                          
                          {/* Arrow */}
                          <div style={{
                            position: 'absolute',
                            top: '-6px',
                            right: '10px',
                            width: '12px',
                            height: '12px',
                            background: '#1f2937',
                            transform: 'rotate(45deg)'
                          }} />
                        </motion.div>
                      )}
                    </div>
                  </div>

                  {/* Amount */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: investment.color, marginBottom: '8px' }}>
                      {formatCurrency(investment.amount)}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      Valor de la inversión
                    </div>
                  </div>

                  {/* Classification Arrow */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: '500', 
                      color: '#6b7280' 
                    }}>
                      Clasificado como:
                    </div>
                    <div style={{ color: investment.color }}>
                      →
                    </div>
                    <div style={{
                      background: `${investment.color}15`,
                      color: investment.color,
                      padding: '6px 12px',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      border: `1px solid ${investment.color}30`
                    }}>
                      {investment.classification}
                    </div>
                  </div>

                  {/* Details */}
                  <div style={{ 
                    background: `${investment.color}08`, 
                    border: `1px solid ${investment.color}20`,
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <Clock size={16} style={{ color: investment.color }} />
                      <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                        Características:
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>
                      • Plazo: {investment.plazo}<br/>
                      • Naturaleza: {investment.naturaleza}<br/>
                      • Liquidez: Alta (corto plazo)
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div style={{
                    marginTop: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    border: '1px solid #10b981',
                    borderRadius: '8px',
                    padding: '8px 12px'
                  }}>
                    <CheckCircle size={16} style={{ color: '#10b981' }} />
                    <span style={{ fontSize: '12px', color: '#065f46', fontWeight: '600' }}>
                      CLASIFICACIÓN CORRECTA
                    </span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Summary Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            border: '2px solid #e2e8f0',
            borderRadius: '16px',
            padding: '24px',
            marginTop: '24px'
          }}
        >
          <h3 style={{ 
            margin: '0 0 20px 0', 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#1f2937',
            textAlign: 'center'
          }}>
            Resumen de Clasificación
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            
            {/* Total Investments */}
            <div style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6', marginBottom: '4px' }}>
                {formatCurrency(totalInvestments)}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Total Inversiones
              </div>
            </div>

            {/* Classification Status */}
            <div style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981', marginBottom: '4px' }}>
                2/2
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Correctamente Clasificadas
              </div>
            </div>

            {/* Investment Type */}
            <div style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#8b5cf6', marginBottom: '4px' }}>
                Temporales
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Tipo de Inversión
              </div>
            </div>
          </div>

          {/* Final Validation */}
          <div style={{
            marginTop: '20px',
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            border: '2px solid #10b981',
            borderRadius: '12px',
            padding: '16px',
            textAlign: 'center'
          }}>
            <CheckCircle size={24} style={{ color: '#10b981', marginBottom: '8px' }} />
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#065f46', marginBottom: '4px' }}>
              Validación Completada
            </div>
            <div style={{ fontSize: '14px', color: '#047857' }}>
              Todas las inversiones han sido clasificadas correctamente según su plazo y naturaleza
            </div>
          </div>
        </motion.div>

        {/* Final Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            border: '1px solid #7dd3fc',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '20px',
            textAlign: 'center'
          }}
        >
          <div style={{ marginBottom: '12px' }}>
            <CheckCircle size={32} style={{ color: '#0ea5e9' }} />
          </div>
          
          <h3 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#0c4a6e' 
          }}>
            ✅ Análisis IA Completado
          </h3>
          
          <p style={{ 
            margin: '0 0 16px 0', 
            fontSize: '14px', 
            color: '#0369a1',
            lineHeight: '1.4'
          }}>
            7 pasos procesados exitosamente • Reclasificación automática completada
          </p>

          {/* Compact Progress indicators */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{
              background: 'white',
              border: '1px solid #86efac',
              borderRadius: '8px',
              padding: '8px 12px',
              minWidth: '80px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#10b981', marginBottom: '2px' }}>
                4/4
              </div>
              <div style={{ fontSize: '10px', color: '#047857' }}>
                Básicos
              </div>
            </div>

            <div style={{
              background: 'white',
              border: '1px solid #c4b5fd',
              borderRadius: '8px',
              padding: '8px 12px',
              minWidth: '80px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#8b5cf6', marginBottom: '2px' }}>
                3/3
              </div>
              <div style={{ fontSize: '10px', color: '#7c3aed' }}>
                Nota 5
              </div>
            </div>

            <div style={{
              background: 'white',
              border: '1px solid #7dd3fc',
              borderRadius: '8px',
              padding: '8px 12px',
              minWidth: '80px'
            }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#0ea5e9', marginBottom: '2px' }}>
                100%
              </div>
              <div style={{ fontSize: '10px', color: '#0369a1' }}>
                Total
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Reclassification; 